import { Alert, Autocomplete, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, Switch, TextField, Typography } from "@mui/material";
import { useState } from "react";

import { useRoleOptions } from "../accounts_user_directory/useData";
import { errorMessage } from "../api/client";
import { EmptyState, ErrorState, LoadingState, PageHeader, PermissionGate } from "../components";
import help from "./HELP.md?raw";
import { useDeleteSso, useSaveSso, useScimToken, useSsoConnections, type SsoConnection } from "./useData";

const EMPTY: Partial<SsoConnection> & { client_secret?: string } = { name: "", protocol: "oidc", is_active: true, enforce: false, email_domains: [], issuer: "", client_id: "", client_secret: "", scopes: "openid email profile", jit_provisioning: true, default_roles: [] };

function ConnectionDialog({ initial, onClose }: { initial: Partial<SsoConnection> & { client_secret?: string }; onClose: () => void }) {
  const roles = useRoleOptions();
  const save = useSaveSso();
  const [form, setForm] = useState({ ...EMPTY, ...initial, client_secret: "" });
  const [error, setError] = useState<string | null>(null);
  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Dialog open onClose={onClose} maxWidth="md">
      <DialogTitle>{initial.id ? `Edit ${initial.name}` : "New SSO connection"}</DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2, pt: 1 }}>
        {error && <ErrorState message={error} />}
        <TextField label="Name" value={form.name} onChange={(e) => set("name", e.target.value)} sx={{ mt: 1 }} autoFocus />
        <TextField select label="Protocol" value={form.protocol} onChange={(e) => set("protocol", e.target.value)} slotProps={{ select: { native: true } }}>
          <option value="oidc">OpenID Connect (Okta, Entra ID, Google, Auth0)</option><option value="saml">SAML 2.0 (requires SAML bridge)</option>
        </TextField>
        <Autocomplete multiple freeSolo options={[]} value={form.email_domains ?? []} onChange={(_, v) => set("email_domains", v)} renderInput={(p) => <TextField {...p} label="Email domains" helperText="Type a domain and press Enter, e.g. corp.example" />} />
        {form.protocol === "oidc" ? (
          <>
            <TextField label="Issuer URL" value={form.issuer} onChange={(e) => set("issuer", e.target.value)} helperText="Discovery document at <issuer>/.well-known/openid-configuration" />
            <TextField label="Client ID" value={form.client_id} onChange={(e) => set("client_id", e.target.value)} />
            <TextField label="Client secret" type="password" value={form.client_secret} onChange={(e) => set("client_secret", e.target.value)} helperText={initial.has_client_secret ? "Leave blank to keep the current secret." : ""} />
            <TextField label="Scopes" value={form.scopes} onChange={(e) => set("scopes", e.target.value)} />
            <Alert severity="info">Redirect URI to register with your IdP: <code>{window.location.origin}/api/v1/accounts/auth/sso/callback/</code></Alert>
          </>
        ) : (
          <>
            <TextField label="IdP metadata URL" value={form.saml_metadata_url} onChange={(e) => set("saml_metadata_url", e.target.value)} />
            <TextField label="Entity ID" value={form.saml_entity_id} onChange={(e) => set("saml_entity_id", e.target.value)} />
          </>
        )}
        <Autocomplete multiple options={roles.data ?? []} getOptionLabel={(r) => r.name} value={(roles.data ?? []).filter((r) => (form.default_roles ?? []).includes(r.id))}
          onChange={(_, v) => set("default_roles", v.map((r) => r.id))} renderInput={(p) => <TextField {...p} label="Default roles for new users" />} />
        <Stack direction="row" spacing={2}>
          <FormControlLabel control={<Switch checked={!!form.jit_provisioning} onChange={(e) => set("jit_provisioning", e.target.checked)} />} label="Create users on first sign-in" />
          <FormControlLabel control={<Switch checked={!!form.enforce} onChange={(e) => set("enforce", e.target.checked)} />} label="Require SSO for these domains" />
          <FormControlLabel control={<Switch checked={!!form.is_active} onChange={(e) => set("is_active", e.target.checked)} />} label="Active" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!form.name || save.isPending} onClick={async () => {
          try { await save.mutateAsync(form as never); onClose(); } catch (err) { setError(errorMessage(err)); }
        }}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

export function SsoSettings() {
  const conns = useSsoConnections();
  const remove = useDeleteSso();
  const scim = useScimToken();
  const [editing, setEditing] = useState<Partial<SsoConnection> | null>(null);
  const [token, setToken] = useState<{ token: string; base_url: string } | null>(null);
  if (conns.isLoading) return <LoadingState />;
  if (conns.isError) return <ErrorState message={(conns.error as Error).message} />;
  return (
    <>
      <PageHeader help={help} title="Single sign-on" subtitle="Let your team sign in with your identity provider and provision users automatically" crumbs={[{ label: "Settings" }, { label: "Single sign-on" }]}
        actions={<PermissionGate permission="accounts.sso.create"><Button variant="contained" onClick={() => setEditing({})}>New connection</Button></PermissionGate>} />
      {conns.data?.length === 0 && <EmptyState title="No SSO connection yet" description="Connect Okta, Microsoft Entra ID, Google Workspace or any OpenID Connect provider." />}
      <Stack spacing={2}>
        {conns.data?.map((c) => (
          <Card key={c.id}><CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <div>
                <Typography variant="h6">{c.name} <Chip size="small" label={c.protocol.toUpperCase()} sx={{ ml: 1 }} /> {!c.is_active && <Chip size="small" label="inactive" />} {c.enforce && <Chip size="small" color="warning" label="enforced" />}</Typography>
                <Typography variant="body2" color="text.secondary">Domains: {c.email_domains.join(", ") || "—"} · Issuer: {c.issuer || c.saml_metadata_url || "—"}</Typography>
                <Typography variant="body2" color="text.secondary">SCIM: {c.scim_enabled ? `enabled (token cfs_${c.scim_token_prefix}…)` : "off"} · JIT provisioning: {c.jit_provisioning ? "on" : "off"}</Typography>
              </div>
              <Stack direction="row" spacing={1}>
                <PermissionGate permission="accounts.sso.change">
                  <Button onClick={() => setEditing(c)}>Edit</Button>
                  <Button onClick={() => scim.mutate(c.id, { onSuccess: setToken })}>{c.scim_enabled ? "Rotate SCIM token" : "Enable SCIM"}</Button>
                </PermissionGate>
                <PermissionGate permission="accounts.sso.delete"><Button color="error" onClick={() => { if (confirm(`Delete ${c.name}?`)) remove.mutate(c.id); }}>Delete</Button></PermissionGate>
              </Stack>
            </Stack>
          </CardContent></Card>
        ))}
      </Stack>
      {editing && <ConnectionDialog initial={editing} onClose={() => setEditing(null)} />}
      <Dialog open={!!token} onClose={() => setToken(null)}>
        <DialogTitle>SCIM provisioning token</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 1 }}>
          <Alert severity="warning">Shown once. Paste it into your IdP's SCIM configuration.</Alert>
          <TextField label="SCIM base URL" value={token?.base_url ?? ""} slotProps={{ input: { readOnly: true } }} />
          <TextField label="Bearer token" value={token?.token ?? ""} slotProps={{ input: { readOnly: true } }} onFocus={(e) => e.target.select()} />
        </DialogContent>
        <DialogActions><Button variant="contained" onClick={() => setToken(null)}>Done</Button></DialogActions>
      </Dialog>
    </>
  );
}
